import Button from '@/components/Buttons/Button';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { IconMessage } from '@/components/IconMessage/IconMessage';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import SingUpModal from '@/components/Modals/SingUpModal';
import UpgradePlanModal from '@/components/Modals/UpgradePlanModal';
import RemoveWatermarkToggle from '@/components/RemoveWatermarkToggle/RemoveWatermarkToggle';
import { toast } from '@/components/toast';
import { minutesToCreditsDuration, pricingStudioUrl } from '@/environment';
import { useFreeVideos } from '@/hooks/useFreeVideos';
import { useRemoveWatermarkAccess } from '@/hooks/useRemoveWatermarkAccess';
import { useUnlimitedVideosEnabled } from '@/hooks/useUnlimitedVideosEnabled';
import DefaultThumbnail from '@/images/ClapperIcon.png';
import flashLightning from '@/images/FlashLightning.svg';
import InfoCircle from '@/images/InfoCircle.svg';
import Presenters from '@/images/SPPlus.png';
import TrashLight from '@/images/TrashLight.svg';
import { createTranslationModalOpen } from '@/pages/translation/state';
import { useAnalytics } from '@/services/analytics';
import { uploadFile } from '@/services/api';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { creditsToVideoMinutes, useCreditQuery } from '@/state/credits/credits';
import { ttsSignupModal, ttsUpgradePlanModal } from '@/state/presenters';
import { useUserSettings } from '@/state/user-settings/UserSettingsProvider';
import theme from '@/theme';
import { AppRoutes } from '@/utils/types';
import { PlanGroup } from '@d-id/types';
import styled from '@emotion/styled';
import { IconButton, Typography } from '@mui/material';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { useCreateTranslation } from '../../hooks/useCreateTranslation';
import translate from '../../images/Translate.svg';
import { LanguageMultiSelect } from '../LanguageMultiSelect';
import { Header } from './Header';
import { ProofreadButton } from './ProofreadButton';
import SourceLanguageSelect from './SourceLanguageSelect';
import { VideoInput } from './VideoInput';

const dialogStyles = {
    root: {
        width: 670,
        padding: '3rem',
        borderRadius: '20px',
    },
};

const VideoUploadSection = styled.section<{ size?: number }>`
    border-radius: 12px;
    width: 100%;
    height: ${({ size }) => `${size}px`};
    background-color: ${({ theme }) => theme.colors.black[100]};
    margin-bottom: 1.4rem;
    padding: 24px;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const TranslateToLabel = styled(Typography)`
    margin-bottom: 0.8rem;
`;

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    margin-block-start: 32px;
`;

const SubmitButton = styled(Button)`
    height: 4.8rem;
    font-size: 18px;
`;

const TrashButton = styled(IconButton)`
    position: absolute;
    right: 8px;
    top: 8px;
`;

const VideoWrapper = styled.div`
    align-items: center;
    justify-content: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: white;
    position: relative;
    border-radius: 12px;
    padding: 32px;
    border: 1px solid ${({ theme }) => theme.colors.secondary[300]};
`;

const StyledIconMessage = styled(IconMessage)`
    margin-bottom: 0.8rem;
    background-color: ${({ theme }) => theme.colors.primary.bgLight};
`;

const StyledLink = styled.a`
    color: ${({ theme }) => theme.colors.primary.main};
    text-decoration: none;
    font-weight: 700;

    &:hover {
        text-decoration: none !important;
        color: ${({ theme }) => theme.colors.grey[700]};
    }
`;

const Thumbnail = styled.img`
    height: 80px;
`;

const VideoTitle = styled(Typography)`
    margin: 0 32px;
    text-align: center;
`;

const UpgradeBanner = styled.div`
    display: flex;
    width: 100%;
    padding: 10px 16px;
    align-items: center;
    gap: 8px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.primary.main};
    background: ${({ theme }) => theme.colors.primary.bgLight};
    margin-block-end: 16px;
`;
const UpgradeText = styled.a`
    color: ${({ theme }) => theme.colors.primary.main};
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.32px;
    text-decoration-line: underline;
`;
const BannerText = styled.span`
    color: ${({ theme }) => theme.colors.black[900]};
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.32px;
`;

const RemoveWatermarkContainer = styled.div`
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    border-radius: 0.8rem;
    display: flex;
    align-items: center;
    padding: 1.6rem;
    width: fit-content;
    margin-top: 1.6rem;
`;

const PremiumBadge = styled(MaskedIcon)`
    margin-left: 0.8rem;
    margin-right: 0.4rem;
`;

const FormBlocker = styled.div`
    position: absolute;
    top: 80px;
    left: 0;
    width: 100%;
    height: calc(100% - 80px);
`;

export function CreateTranslationModal() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [open, setOpen] = useRecoilState(createTranslationModalOpen);
    const [signupModalState, setSignupModalState] = useRecoilState(ttsSignupModal);
    const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
    const [sourceLanguage, setSourceLanguage] = useState<string | null>(null);
    const [upgradeModalState, setUpgradeModalState] = useRecoilState(ttsUpgradePlanModal);
    const { isAnonymous, user } = useAuth();
    const uploadPromiseRef = useRef<Promise<string> | null>(null);
    const sourceLanguageEnabled = useFlag('translations_source_language_enabled', false);
    const createTranslationMutation = useCreateTranslation();
    const signalControllerRef = useRef<AbortController | null>(null);
    const uploadFileMutation = useMutation(
        ({ file, signal }: { file: File; signal: AbortSignal }) => uploadFile(file, signal, { s3Uri: true }),
        {
            onError: error => {
                const isCanceled = axios.isAxiosError(error) && error.name === 'CanceledError';
                if (isCanceled) {
                    return;
                }

                handleClearFile();
                toast.error({ title: 'Failed to upload file' });
                logger.error('Failed to upload file', error);
            },
        }
    );

    const [file, setFile] = useState<File | null>(null);
    const [thumbnail, setThumbnail] = useState<string | undefined>();
    const [title, setTitle] = useState<string | undefined>();
    const [videoDuration, setVideoDuration] = useState<number | undefined>();
    const [unsupportedDuration, setUnsupportedDuration] = useState<number | undefined>();
    const [isBrowserSupportingFormat, setIsBrowserSupportingFormat] = useState<boolean>(true);
    const { userSettings, updateUserSettings, userSettingsUpdating } = useUserSettings();
    const initialRemoveWatermarkSelected = useRef<boolean>(!!userSettings?.preferences?.removeWatermark);
    const [removeWatermarkSelected, setRemoveWatermarkSelected] = useState<boolean>(!!userSettings?.preferences?.removeWatermark);
    const { showRemoveWatermark, removeWatermarkBlocked } = useRemoveWatermarkAccess(user);
    const isTrialOrFreeUser = user?.planGroup === PlanGroup.TRIAL || user?.planGroup === PlanGroup.FREE;
    const creditsToMinutesEnabled = useFlag('credits_to_minutes_enabled', false);
    const isFreeUser = user?.planGroup === PlanGroup.FREE;
    const isTrialUser = user?.planGroup === PlanGroup.TRIAL;
    const { data: { remaining: freeVideos } = { remaining: 0 }, isFetched: isFreeVideosFetched, outOfFreeVideos } = useFreeVideos();

    const { enabled: unlimitedVideoCreationEnabled, isFetched: unlimitedVideoCreationIsFetched } = useUnlimitedVideosEnabled(user);

    const mixpanel = useAnalytics();
    const creditsCost =
        videoDuration && selectedLanguages.length > 0 ? Math.ceil(videoDuration / minutesToCreditsDuration) * selectedLanguages.length : 0;
    const { data: credits, isFetched: isCreditsFetched } = useCreditQuery({ enabled: user?.planGroup !== PlanGroup.FREE });

    const processingVideoFile = (file: File) => {
        const fileUrl = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.src = fileUrl;
        video.onloadedmetadata = () => {
            video.currentTime = 0.01;
            const allowedDuration = getAllowedDuration(video.duration);
            setVideoDuration(allowedDuration);
        };
        video.onseeked = () => {
            captureThumbnail(video);
            setTitle(file.name);
        };
        video.onerror = err => {
            console.error('Error loading video', err);
        };
    };

    useEffect(() => {
        // when you are redirected to translation pages with "create" query param, open the modal
        if (searchParams.has('create')) {
            setOpen(true);
            setSearchParams({});
        }
    }, []);

    useEffect(() => {
        if (open && isAnonymous) {
            setSignupModalState(true);
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }

        if (outOfFreeVideos) {
            setUpgradeModalState(true);
        }
    }, [open, isAnonymous, outOfFreeVideos]);

    useEffect(() => {
        if (file) {
            if (!isBrowserSupportingFormat) {
                if (!unsupportedDuration) {
                    return;
                }
                setTitle(file.name);
                setThumbnail(DefaultThumbnail);
                const allowedDuration = getAllowedDuration(unsupportedDuration);
                setVideoDuration(allowedDuration);
                return;
            }

            processingVideoFile(file);
        } else {
            setUnsupportedDuration(undefined);
            setThumbnail(undefined);
            setTitle(undefined);
        }
    }, [file, isBrowserSupportingFormat, unsupportedDuration]);

    useEffect(() => {
        if (removeWatermarkSelected && showRemoveWatermark && !removeWatermarkBlocked) {
            updateUserSettings({ preferences: { removeWatermark: true } }).catch(() => {
                setRemoveWatermarkSelected(false);
                toast.error({ title: 'Failed to update watermark preference, please try again' });
            });
        }
    }, [removeWatermarkSelected, showRemoveWatermark, removeWatermarkBlocked]);

    function getAllowedDuration(duration: number) {
        return isTrialOrFreeUser ? Math.min(30, duration) : duration;
    }

    function captureThumbnail(video: HTMLVideoElement) {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            try {
                const thumbnailUrl = canvas.toDataURL('image/png');
                setThumbnail(thumbnailUrl);
            } catch (error) {
                logger.error('[captureThumbnail] failed to create data url', error);
                throw error;
            }
        }
    }

    const handleSubmit = async (options?: { proofread?: boolean }) => {
        if (!uploadPromiseRef.current || !file || !thumbnail) {
            logger.error('Trying to submit a translation without a file or thumbnail', {
                file,
                thumbnail,
                uploadPromiseRef: uploadPromiseRef.current,
            });
            return;
        }

        const mixpanelProps = {
            languages: selectedLanguages,
            num_languages: selectedLanguages.length,
            duration: videoDuration,
            isBrowserSupportingFormat,
            proofread: options?.proofread ? true : false,
            credits: unlimitedVideoCreationEnabled ? 0 : creditsCost,
            source_language: sourceLanguage,
        };
        const trackEventName = options?.proofread ? 'translate-proofread' : 'translate-video';

        mixpanel?.track(trackEventName, {
            event: 'click',
            ...mixpanelProps,
        });

        let routeToNavigate: string = AppRoutes.Translation;
        const payload = {
            uploadFilePromise: uploadPromiseRef.current,
            selectedLanguages,
            sourceLanguage,
            file,
            thumbnail_url: thumbnail,
            options: {
                // Because thumbnail is created only after validation, we would like library to refetch only after validation is over
                // Library gets updated on translation/group-started and translation/started
                skipRefetch: true,
            },
            mixpanelProps,
        };

        if (options?.proofread) {
            const { translations } = await createTranslationMutation.mutateAsync(
                { ...payload, proofreading: true },
                {
                    onSettled: () => {
                        uploadPromiseRef.current = null;
                    },
                }
            );

            const groupId = translations[0].group_id;

            routeToNavigate = `${AppRoutes.TranslationDraft}/${groupId}`;
        } else {
            createTranslationMutation.mutate(payload, {
                onSettled: () => {
                    uploadPromiseRef.current = null;
                },
            });
        }

        navigate(routeToNavigate);
        setOpen(false);
        handleReset({ abortPreviousUploads: false });
    };

    const handleReset = ({ abortPreviousUploads = true } = {}) => {
        handleClearFile({ abortPreviousUploads });
        setSelectedLanguages([]);
        setSourceLanguage(null);
    };

    const handleClearFile = ({ abortPreviousUploads = true } = {}) => {
        setFile(null);
        setVideoDuration(undefined);
        setIsBrowserSupportingFormat(true);
        setUnsupportedDuration(undefined);
        setThumbnail(undefined);
        setTitle(undefined);

        if (abortPreviousUploads) {
            clearUpload();
        }
    };

    const clearUpload = () => {
        if (uploadPromiseRef.current) {
            uploadPromiseRef.current = null;
        }
        if (signalControllerRef.current) {
            signalControllerRef.current.abort();
            signalControllerRef.current = null;
        }
    };

    const handleUploadFile = async (file: File): Promise<string> => {
        setFile(file);

        const nextController = new AbortController();
        signalControllerRef.current = nextController;

        uploadPromiseRef.current = uploadFileMutation.mutateAsync({ file, signal: nextController.signal });
        return uploadPromiseRef.current;
    };

    const isVideoChosen = !!file && !!thumbnail && !!title;

    if (signupModalState) {
        return (
            <SingUpModal
                text="Sign up to translate your videos"
                mainText="Create an account to translate videos and unlock additional features"
                from="translations"
                isOpen
                onClose={() => {
                    setSignupModalState(false);
                    setOpen(false);
                }}
            />
        );
    }

    if (upgradeModalState) {
        return (
            <UpgradePlanModal
                open={upgradeModalState}
                onClose={() => {
                    setUpgradeModalState(false);
                    setOpen(false);
                }}
                title={<div>Upgrade to a Pro Plan to Access Video Translate</div>}
                imageFile={Presenters}
                features={[
                    'Additional credits',
                    'AI generated Avatars',
                    'Access to plugins',
                    'API access',
                    '100+ Languages & accents',
                    'Auto generated scripts',
                ]}
                from="increase packages modal"
            />
        );
    }

    const hasEnoughCredits =
        (isCreditsFetched && unlimitedVideoCreationIsFetched && !!credits && credits.remaining >= creditsCost) ||
        (freeVideos >= selectedLanguages.length && isFreeVideosFetched);
    const shouldDisable =
        !file || !selectedLanguages.length || !hasEnoughCredits || (!isBrowserSupportingFormat && file && !unsupportedDuration);
    const costText = creditsToMinutesEnabled ? `${creditsToVideoMinutes(creditsCost)} Minutes` : `${creditsCost} Credits`;

    const proofreadingSubmitting = createTranslationMutation.isLoading && createTranslationMutation.variables?.proofreading;
    const translateSubmitting = createTranslationMutation.isLoading && !createTranslationMutation.variables?.proofreading;
    const shouldBlockForm = createTranslationMutation.isLoading;

    return (
        <BootstrapDialog rootstyle={dialogStyles.root} open={open} id="translation-modal">
            <Header onClose={handleReset} />
            {isVideoChosen ? (
                <VideoUploadSection id="translation-uploaded-section">
                    <VideoWrapper>
                        <TrashButton onClick={() => handleClearFile()}>
                            <MaskedIcon src={TrashLight} size="24px" />
                        </TrashButton>
                        <Thumbnail src={thumbnail} />
                        <VideoTitle variant="h4">{title}</VideoTitle>
                    </VideoWrapper>
                </VideoUploadSection>
            ) : (
                <VideoUploadSection size={236} id="translation-upload-section">
                    <VideoInput
                        handleUploadFile={handleUploadFile}
                        setUnsupportedDuration={setUnsupportedDuration}
                        isBrowserSupportingFormat={isBrowserSupportingFormat}
                        setIsBrowserSupportingFormat={setIsBrowserSupportingFormat}
                    />
                </VideoUploadSection>
            )}

            {isTrialOrFreeUser && (
                <StyledIconMessage icon={InfoCircle} iconColor={theme.colors.primary.main}>
                    <p>
                        We will translate the first 30 seconds of your video.{' '}
                        <StyledLink href={pricingStudioUrl} target="_blank" rel="noopener noreferrer">
                            Upgrade your plan
                        </StyledLink>{' '}
                        to translate entire videos.
                    </p>
                </StyledIconMessage>
            )}

            {sourceLanguageEnabled && (
                <>
                    <TranslateToLabel variant="h4">Source language</TranslateToLabel>
                    <SourceLanguageSelect value={sourceLanguage} onChange={setSourceLanguage} />
                </>
            )}
            <TranslateToLabel variant="h4">Translate to</TranslateToLabel>
            <LanguageMultiSelect
                selectedLanguages={selectedLanguages}
                setSelectedLanguages={setSelectedLanguages}
                useDisabledOptions={
                    (selectedLanguages.length >= 3 && isTrialUser) || (isFreeUser && selectedLanguages.length >= freeVideos)
                }
            />

            {!hasEnoughCredits && (
                <UpgradeBanner>
                    <MaskedIcon src={InfoCircle} size="32px" color={theme.colors.primary.main} />
                    <BannerText>
                        You don't have enough {creditsToMinutesEnabled ? 'minutes' : 'credits'} to translate this video.{' '}
                        <UpgradeText href={pricingStudioUrl}>Upgrade now</UpgradeText>
                    </BannerText>
                </UpgradeBanner>
            )}

            {showRemoveWatermark && !initialRemoveWatermarkSelected.current && (
                <RemoveWatermarkContainer>
                    <RemoveWatermarkToggle
                        checked={removeWatermarkSelected}
                        onChange={enabled => {
                            setRemoveWatermarkSelected(enabled);
                        }}
                        blocked={removeWatermarkBlocked}
                    />
                    <PremiumBadge
                        src={flashLightning}
                        size="16px"
                        color={removeWatermarkBlocked ? theme.colors.primary.main : theme.colors.secondary.main}
                    />
                    <Typography variant="body1" fontWeight={500}>
                        Remove watermark
                    </Typography>
                </RemoveWatermarkContainer>
            )}

            {shouldBlockForm && <FormBlocker />}

            <ButtonsContainer>
                <ProofreadButton
                    onClick={() => handleSubmit({ proofread: true })}
                    disabled={userSettingsUpdating || shouldDisable || translateSubmitting}
                    isLoading={proofreadingSubmitting}
                />
                <SubmitButton
                    data-testid="translation-modal__translate-button"
                    startIcon={<MaskedIcon src={translate} size="24px" />}
                    onClick={() => handleSubmit()}
                    disabled={shouldDisable || proofreadingSubmitting}
                    isLoading={userSettingsUpdating || translateSubmitting}>
                    Translate video
                    {creditsCost ? (unlimitedVideoCreationEnabled && hasEnoughCredits ? ' (Free)' : ` (${costText})`) : ''}
                </SubmitButton>
            </ButtonsContainer>
        </BootstrapDialog>
    );
}
